import React, { useEffect, useMemo, useState } from 'react';
import {
  Container, Typography, Paper, Box, CircularProgress, Grid,
  TextField, InputAdornment, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
  Chip, Stack, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase';
import { wouldReceive } from '../../../utils/notificationEligibility';

const PREFERENCES = [
  ['newGames', 'New Games'],
  ['leaderboardUpdate', 'Leaderboard'],
  ['kickoffReminder', 'Kickoff'],
];

function compareUsers(a, b, key) {
  if (key === 'phoneVerified') {
    // verified=true should sort first when ascending
    return (b.phoneVerified === true) - (a.phoneVerified === true);
  }
  const av = (a[key] || '').toString().toLowerCase();
  const bv = (b[key] || '').toString().toLowerCase();
  if (av < bv) return -1;
  if (av > bv) return 1;
  return 0;
}

// TODO(iteration 2): replace this no-op with the user-detail drawer trigger
// (admin role toggle, opt-out override, targeted SMS, activity log).
// See docs/brainstorms/2026-05-21-user-management-requirements.md.
const handleRowClick = () => {};

function UserManagement() {
  const theme = useTheme();
  // Phones get cards instead of the six-column table (same breakpoint as
  // the header drawer). Search still applies; the order is whatever sort was
  // last chosen in the table's headers (name ascending by default).
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [users, setUsers] = useState(null);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sort, setSort] = useState({ key: 'displayName', direction: 'asc' });
  const loading = users === null && !error;

  // Live subscription so server-side writes (STOP webhook flipping smsOptOut,
  // verifyPhoneCode flipping phoneVerified) surface without a refresh.
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'users'),
      (snap) => {
        setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setError(null);
      },
      (err) => {
        console.error('UserManagement: users snapshot listener error:', err);
        setError(err.message || 'Failed to load users.');
      }
    );
    return unsubscribe;
  }, []);

  const handleSort = (key) => {
    setSort((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const filteredSortedUsers = useMemo(() => {
    if (users === null) return [];
    const q = searchQuery.trim().toLowerCase();
    const filtered = q
      ? users.filter((u) =>
          (u.displayName || '').toLowerCase().includes(q) ||
          (u.email || '').toLowerCase().includes(q)
        )
      : users;
    const dir = sort.direction === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => dir * compareUsers(a, b, sort.key));
  }, [users, searchQuery, sort]);

  const sortHeader = (key, label) => (
    <TableCell sortDirection={sort.key === key ? sort.direction : false}>
      <TableSortLabel
        active={sort.key === key}
        direction={sort.key === key ? sort.direction : 'asc'}
        onClick={() => handleSort(key)}
      >
        <strong>{label}</strong>
      </TableSortLabel>
    </TableCell>
  );

  const renderEligibilityChip = (user, prefKey, label) => {
    const eligible = wouldReceive(user, prefKey);
    return (
      <Chip
        key={prefKey}
        label={label}
        size="small"
        variant="outlined"
        color={eligible ? 'success' : 'default'}
        icon={eligible ? <CheckCircleIcon aria-hidden /> : undefined}
        data-eligible={eligible ? 'true' : 'false'}
        sx={{ mr: 0.5, mb: 0.5 }}
      />
    );
  };

  const renderCards = () => (
    filteredSortedUsers.length === 0 ? (
      <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
        {users.length === 0 ? 'No users registered yet.' : 'No users match your search.'}
      </Paper>
    ) : (
      <Stack spacing={1.5} role="list" aria-label="Users">
        {filteredSortedUsers.map((user) => (
          <Paper key={user.id} role="listitem" data-testid="user-card" sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1, minWidth: 0 }} noWrap>
                {user.displayName || user.email || '—'}
              </Typography>
              {user.smsOptOut === true && <Chip size="small" color="warning" variant="outlined" label="Opted out" />}
            </Box>
            {user.displayName && (
              <Typography variant="body2" color="text.secondary" noWrap>{user.email || '—'}</Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
              {user.phoneVerified === true ? (
                <CheckCircleIcon color="success" fontSize="small" aria-label="Verified" />
              ) : (
                <ErrorIcon color="disabled" fontSize="small" aria-label="Not verified" />
              )}
              <Typography variant="body2" color="text.secondary">{user.phoneNumber || 'No phone'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 1 }}>
              {PREFERENCES.map(([key, label]) => renderEligibilityChip(user, key, label))}
            </Box>
          </Paper>
        ))}
      </Stack>
    )
  );

  return (
    <Container maxWidth="lg" style={{ marginTop: '20px' }}>
      <Typography variant="h5" gutterBottom>Users</Typography>
      {error && (
        <Typography color="error" gutterBottom>{error}</Typography>
      )}

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by display name or email"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => setSearchQuery('')}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
        />
      </Box>

      {loading ? (
        <Grid container justifyContent="center" sx={{ mt: 4 }}>
          <CircularProgress />
        </Grid>
      ) : users === null ? null : isMobile ? renderCards() : (
        <Paper sx={{ overflowX: 'auto' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {sortHeader('displayName', 'Display Name')}
                  {sortHeader('email', 'Email')}
                  <TableCell><strong>Phone</strong></TableCell>
                  {sortHeader('phoneVerified', 'Verified')}
                  <TableCell><strong>Opted Out</strong></TableCell>
                  <TableCell><strong>Would Receive</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSortedUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                      {users.length === 0
                        ? 'No users registered yet.'
                        : 'No users match your search.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSortedUsers.map((user) => (
                    <TableRow
                      key={user.id}
                      hover
                      onClick={handleRowClick}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{user.displayName || '—'}</TableCell>
                      <TableCell>{user.email || '—'}</TableCell>
                      <TableCell>{user.phoneNumber || '—'}</TableCell>
                      <TableCell>
                        {user.phoneVerified === true ? (
                          <CheckCircleIcon color="success" fontSize="small" aria-label="Verified" />
                        ) : (
                          <ErrorIcon color="disabled" fontSize="small" aria-label="Not verified" />
                        )}
                      </TableCell>
                      <TableCell>{user.smsOptOut === true ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                          {PREFERENCES.map(([key, label]) => renderEligibilityChip(user, key, label))}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Container>
  );
}

export default UserManagement;
